import React, { createContext, useContext, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full85";

interface ModalContextValue {
  onHide: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export interface ModalProps {
  show: boolean;
  onHide: () => void;
  size?: ModalSize;
  centered?: boolean;
  children?: React.ReactNode;
  dialogClassName?: string;
}

const getSizeClasses = (size: ModalSize | undefined): string => {
  switch (size) {
    case "sm":
      return "max-w-sm w-full";
    case "md":
      return "max-w-md w-full";
    case "lg":
      return "max-w-3xl w-full";
    case "xl":
      return "max-w-5xl w-full";
    case "full85":
      return "w-[85vw] max-w-none";
    default:
      return "max-w-3xl w-full";
  }
};

function useEscapeToClose(enabled: boolean, onHide: () => void) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onHide();
    };
    window.addEventListener("keydown", handler as any);
    return () => window.removeEventListener("keydown", handler as any);
  }, [enabled, onHide]);
}

/** Root modal component rendering a centered overlay dialog with backdrop and configurable size. */
export const ModalRoot: React.FC<ModalProps> = ({
  show,
  onHide,
  size,
  centered,
  children,
  dialogClassName,
}) => {
  useEscapeToClose(show, onHide);

  if (!show) return null;

  const containerAlign = centered ? "items-center" : "items-start";

  // Portal to <body>: the app sidebar is `relative z-[1000]`, so a modal rendered inside the content
  // subtree can never paint above it (its z-index is scoped below that sibling). Rendering at body level
  // lets the backdrop cover the WHOLE viewport, sidebar included.
  return createPortal(
    <ModalContext.Provider value={{ onHide }}>
      <div className={`fixed inset-0 z-[1300] flex ${containerAlign} justify-center p-6`}>
        <div
          className="absolute inset-0 bg-black/50"
          onClick={onHide}
          aria-hidden="true"
        />
        <div
          className={`relative flex max-h-[95vh] flex-col rounded-xl bg-white shadow-2xl ${getSizeClasses(
            size
          )} ${dialogClassName ?? ""}`}
          role="dialog"
          aria-modal="true"
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>,
    document.body
  );
};

interface ModalSectionProps {
  children?: React.ReactNode;
  className?: string;
}

interface ModalHeaderProps extends ModalSectionProps {
  closeButton?: boolean;
}

/** Modal header section with an optional close button. */
const Header: React.FC<ModalHeaderProps> = ({ children, className, closeButton }) => {
  const ctx = useContext(ModalContext);
  return (
    <div className={`flex items-center justify-between border-b px-4 py-3 ${className ?? ""}`}>
      <div className="min-w-0">{children}</div>
      {closeButton && (
        <button
          type="button"
          aria-label="Close"
          onClick={() => ctx?.onHide()}
          className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <span className="text-2xl leading-none">×</span>
        </button>
      )}
    </div>
  );
};

/** Modal title rendered as a truncated h3 heading. */
const Title: React.FC<ModalSectionProps> = ({ children, className }) => {
  return <h3 className={`text-lg font-semibold truncate ${className ?? ""}`}>{children}</h3>;
};

interface ModalBodyProps extends ModalSectionProps {
  scrollable?: boolean;
}

/** Scrollable modal body section. */
const Body: React.FC<ModalBodyProps> = ({ children, className, scrollable = true }) => {
  const overflowClass = scrollable ? "overflow-y-auto" : "overflow-y-visible";
  return <div className={`flex-1 ${overflowClass} px-4 py-4 ${className ?? ""}`}>{children}</div>;
};

/** Modal footer section, typically used for action buttons. */
const Footer: React.FC<ModalSectionProps> = ({ children, className }) => {
  return (
    <div className={`flex items-center justify-end gap-2 border-t px-4 py-3 ${className ?? ""}`}>
      {children}
    </div>
  );
};

/** Compound modal component with Header, Title, Body, and Footer sub-components. */
export const Modal = Object.assign(ModalRoot, {
  Header,
  Title,
  Body,
  Footer,
});



